import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output} from '@angular/core';
import {SessionStorage} from 'ngx-webstorage';
import {Item, Status} from '../../project/shared/project-model';
import {CharacteristicView, MultiOption, Option} from '../shared/characteristics.view';
import {TranslateService} from '@ngx-translate/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MultiSelect} from 'primeng/multiselect';

@Component({
  standalone: false,
  selector: 'app-characteristic-fieldset',
  templateUrl: './characteristic-fieldset.component.html',
  styleUrls: ['./characteristic-fieldset.component.less'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({
        'height': '0px', opacity: 0
      })),
      transition('void <=> *', animate(150)),
    ])
  ]
})
export class CharacteristicFieldsetComponent implements OnInit, OnChanges {

  @Input()
  listOfElement: CharacteristicView[] = [];

  @Input()
  title: string;

  @Input()
  accessInfo: string;

  @Input()
  readOnly = false;

  @Input()
  display = true;

  @Output()
  displayChange = new EventEmitter<boolean>();

  @SessionStorage()
  currentItemct: Item;

  @Output()
  changeValue = new EventEmitter<CharacteristicView>();

  private multiSelectPendingElements = new Set<string>();

  @Output()
  clickOnLast = new EventEmitter();

  chosenOptionsMultiselectModel: {[key: string]: any[]} = {};

  private valueTooltipTrigger: HTMLElement | undefined;

  // Expose Status enum to the view
  Status = Status;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.convertToMultiSelect();
  }

  ngOnChanges() {
    this.listOfElement.filter(elem => elem.htmlElementType === 'multiSelect').forEach(elem => {
      elem.options = this.getElementOptionsSorted(elem);
      elem.multiOptions = elem.options.map(opt => new MultiOption(this.translateService.instant(<string>opt.value), opt.value, 1));
      this.chosenOptionsMultiselectModel[elem.id] = elem.options.filter(o => o.selected).map(o => o.value);
    });
  }

  getElementsSorted(){
    return this.listOfElement.sort((a, b) => a.orderIndex - b.orderIndex);
  }

  getElementOptionsSorted(element: CharacteristicView) {
    if (this.currentItemct?.range === null){
      return element.options;
    }
    return element.options.sort((a, b) => a.orderIndex - b.orderIndex);
  }

  onChangeValue(event, element: CharacteristicView) {
    if (!!element.options) {
      element.options.forEach(opt => opt.selected = String(opt.value) === event);
    }
    if (!!element.packageOptions) {
      element.packageOptions.forEach(opt => opt.selected = String(opt.id) === event);
    }
    element.chooseValue = true;
    this.changeValue.emit(element);
  }

  onMultiSelectChange(event: {value: any[]}, element: CharacteristicView, multiSelect?: MultiSelect) {
    element.options.forEach(opt => opt.selected = event.value.includes(opt.value));
    element.chooseValue = event.value.length > 0;
    this.multiSelectPendingElements.add(element.id);
    setTimeout(() => multiSelect?.overlayViewChild?.alignOverlay(), 0);
  }

  onMultiSelectPanelHide(element: CharacteristicView) {
    if (this.multiSelectPendingElements.has(element.id)) {
      this.multiSelectPendingElements.delete(element.id);
      this.changeValue.emit(element);
    }
  }

  isHtmlElement(element: CharacteristicView, type: string): boolean {
    return !!element.options && element.options.length >= 2 && element.htmlElementType === type;
  }

  valueAlreadyExists(element: CharacteristicView): boolean {
    if (!!element.options) {
      return element.options.some(opt => opt.selected);
    }
    if (!!element.packageOptions) {
      return element.packageOptions.some(opt => opt.selected);
    }
    return false;
  }

  isClickOnLast(isLast: boolean) {
    if (isLast) {
      this.clickOnLast.emit();
    }
  }

  isOnlyOneAvailableOption(element: CharacteristicView): boolean {
    return  !!element.options && element.options.filter(opt => !opt.disabled).length === 1;
  }

  noValueOrHidden(element: CharacteristicView): boolean {
    return !element.chooseValue && !!element.options && element.options.filter(opt => !opt.disabled).length === 0 ||
      !element.chooseValue && !!element.packageOptions && element.packageOptions.length === 0 ||
      element.htmlElementType === 'hidden';
  }

  getOnlyAvailableOption(element: CharacteristicView): Option {
    return !!element.options ? element.options.find(opt => !opt.disabled) : null;
  }

  resetValue(element: CharacteristicView) {
    element.chooseValue = false;
    if (!!element.options) {
      element.options.forEach(opt => opt.selected = false);
    }
    if (!!element.packageOptions) {
      element.packageOptions.forEach(opt => opt.selected = false);
    }
    if (element.htmlElementType === 'multiSelect') {
      this.chosenOptionsMultiselectModel[element.id] = [];
    }
    this.changeValue.emit(element);
  }

  displayAccessories() {
    return (this.listOfElement.length > 0 && this.listOfElement.filter(element => !this.noValueOrHidden(element)).length > 0);
  }

  changeDisplay() {
    this.display = !this.display;
    this.displayChange.emit(this.display);
  }

  private convertToMultiSelect() {
    this.listOfElement.filter(elem => elem.htmlElementType === 'multiSelect').forEach(elem => {
      elem.multiOptions = elem.options.map(opt => new MultiOption(this.translateService.instant(<string>opt.value), opt.value, opt.quantity));
      this.chosenOptionsMultiselectModel[elem.id] = elem.options.filter(o => o.selected).map(o => o.value);
    });
  }

}
